/**
 * Tenant data hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/use-auth';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

interface UseTenantsOptions {
  includeInactive?: boolean;
}

export function useTenants({ includeInactive = false }: UseTenantsOptions = {}) {
  const { activeOrganizationId } = useAuth();

  return useQuery({
    queryKey: ['tenants', activeOrganizationId, includeInactive ? 'all' : 'active'],
    queryFn: async () => {
      if (!activeOrganizationId) return [];
      let query = supabase
        .from('tenants')
        .select('*')
        .eq('organization_id', activeOrganizationId)
        .order('name');

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!activeOrganizationId,
  });
}

export function useTenant(tenantId: string | null) {
  return useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', tenantId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();
  const { activeOrganizationId } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      name: string;
      slug: string;
      timezone?: string;
      locale?: string;
      metadata?: Json;
    }) => {
      if (!activeOrganizationId) throw new Error('No organization');
      const { data, error } = await supabase
        .from('tenants')
        .insert({ ...input, organization_id: activeOrganizationId })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant created');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: {
      id: string;
      name?: string;
      slug?: string;
      timezone?: string;
      locale?: string;
      is_active?: boolean;
      metadata?: Json;
    }) => {
      const { data, error } = await supabase
        .from('tenants')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      toast.success('Tenant updated');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
